// Lógica de armado de datos para "Comparación 3 años" e "Intensidad por
// tipo" de FuelsKPIsDashBoard.js, extraída a un módulo aparte (sin JSX/
// hooks) para poder reusarla también desde el export a PDF
// (pdfFuelsCharts.js) sin duplicar la lógica ni crear un import circular
// con FuelsKPIsDashBoard.js. Mismo patrón que EnergyKPIsDashboard/energyChartData.js.
//
// A diferencia de Energía, acá cada gráfico tiene un selector de tipo de
// combustible — las funciones reciben el combustible seleccionado como
// parámetro en vez de leerlo de un useState local, para poder llamarlas
// tanto en pantalla (con el valor del selector) como desde el PDF (con el
// combustible que estaba seleccionado en pantalla al exportar).

import { convertEnergy2 } from "../../../../utils/dataAndFormulas/convertEnergy";

const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function buildYearColorMap(years) {
  const getCssVar = (name) =>
    getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  const chartColors = {
    oldest: getCssVar("--chart-year-oldest"),
    prev: getCssVar("--chart-year-prev"),
    last: getCssVar("--chart-year-last"),
  };
  const sorted = [...years].map(Number).sort((a, b) => a - b);
  return {
    [sorted[0]]: chartColors.oldest || "#cbd5e1",
    [sorted[1]]: chartColors.prev || "#94a3b8",
    [sorted[2]]: chartColors.last || "#1e293b",
  };
}

export function buildSharedYScale(...dataArrays) {
  const combinedMax = Math.max(
    0,
    ...dataArrays.flatMap((arr) => arr.map((d) => d.value || 0)),
  );
  const yDomainMax = combinedMax > 0 ? Math.ceil(combinedMax * 1.15) : undefined;
  return { domainMin: 0, domainMax: yDomainMax, nice: true };
}

// "Comparación 3 años" ─────────────────────────────────────────────────────

export const FUELS_COMPARISON_OPTIONS = [
  { key: "natural_gas", label: "Gas Natural", color: "#10b981" },
  { key: "glp", label: "GLP", color: "#f59e0b" },
  { key: "diesel", label: "Diesel", color: "#ef4444" },
  { key: "biodiesel", label: "Biodiesel", color: "#22c55e" },
  { key: "gasoline", label: "Gasolina", color: "#3b82f6" },
  { key: "ethanol", label: "Etanol", color: "#8b5cf6" },
  { key: "electricity_vehicles", label: "Electricidad Vehículos", color: "#06b6d4" },
  { key: "total", label: "Totales", color: "#374151" },
];

// Combustibles con datos en algún mes de los últimos 3 años — misma lógica
// que ya usaba el useMemo de FuelsComparisonChart. "Totales" siempre entra.
export function getFuelsComparisonOptions(resultsByYear, years) {
  return FUELS_COMPARISON_OPTIONS.filter((fuel) => {
    if (fuel.key === "total") return true;
    return years.some((year) =>
      months.some((month) => (resultsByYear?.[year]?.[month]?.[fuel.key]?.total || 0) > 0)
    );
  });
}

// resultsByYear -> { years, fuels } — metadata necesaria para el selector,
// disponible ANTES de saber qué combustible está seleccionado.
export function buildFuelsComparisonBase(resultsByYear) {
  const allYears = Object.keys(resultsByYear || {}).map(Number).sort((a, b) => a - b);
  const years = allYears.slice(-3).map(String);
  const fuels = getFuelsComparisonOptions(resultsByYear, years);
  return { years, fuels };
}

// resultsByYear + combustible seleccionado -> series del gráfico. Si
// selectedFuel es null/undefined (todavía no se calculó el default), vuelve
// con chartData/averageData vacíos.
export function buildFuelsComparisonData(resultsByYear, t, selectedFuel) {
  const { years, fuels } = buildFuelsComparisonBase(resultsByYear);
  const chartData = [];
  const averageData = [];

  if (selectedFuel) {
    years.forEach((year) => {
      let yearlyValues = [];

      months.forEach((month) => {
        const monthData = resultsByYear?.[year]?.[month] || {};
        let value = 0;
        if (selectedFuel === "total") {
          value = FUELS_COMPARISON_OPTIONS
            .filter((f) => f.key !== "total")
            .reduce((acc, f) => acc + (monthData?.[f.key]?.total || 0), 0);
        } else {
          value = monthData?.[selectedFuel]?.total || 0;
        }

        if (value > 0) {
          chartData.push({ year, month: t(month), value, tipo: "Consumo", type: `Consumo ${year}` });
        }
        yearlyValues.push(value);
      });

      const currentYear = new Date().getFullYear();
      const isCurrentYear = Number(year) === currentYear;
      const validMonthsCount = yearlyValues.filter((v) => v > 0).length;
      const divisor = isCurrentYear ? (validMonthsCount || 1) : 12;
      const avg = yearlyValues.reduce((sum, v) => sum + v, 0) / divisor;

      months.forEach((month) => {
        averageData.push({ year, month: t(month), value: avg, tipo: "Promedio", type: `Promedio ${year}` });
      });
    });
  }

  return { years, fuels, chartData, averageData, yearColorMap: buildYearColorMap(years) };
}

// "Intensidad por tipo" ─────────────────────────────────────────────────────

const ALL_FUEL_TYPES = {
  natural_gas: { c: "natural_gas", v: "natural_gas_vehicles", label: "Gas Natural", color: "#3b82f6" },
  glp: { c: "glp", v: "glp_vehicles", label: "GLP", color: "#10b981" },
  diesel: { c: "diesel", v: "diesel_vehicles", label: "Diesel", color: "#f59e0b" },
  biodiesel: { c: "biodiesel", v: "biodiesel_vehicles", label: "Biodiesel", color: "#8dd1e1" },
  gasoline: { c: "gasoline", v: "gasoline_vehicles", label: "Gasolina", color: "#d084d0" },
  ethanol: { c: "ethanol", v: "ethanol_vehicles", label: "Etanol", color: "#ff8042" },
  electricity: { c: "electricity_vehicles", v: "electric_vehicles", label: "Eléctrico", color: "#00C49F" },
};

function calcIntensity(monthData, cfg) {
  if (!monthData) return 0;
  const f = monthData?.[cfg.c];
  const v = monthData?.[cfg.v];
  const vehicles = v?.total || 0;
  if (!f?.total || vehicles <= 0) return 0;
  const fuelKWh = f.unit === "kWh" ? f.total : convertEnergy2(f.total, f.unit, "kWh", cfg.c);
  return (fuelKWh || 0) / vehicles;
}

// resultsByYear -> { years, fuelTypes } — metadata necesaria para el
// selector, disponible ANTES de saber qué combustible está seleccionado.
export function buildFuelsIntensityBase(resultsByYear) {
  const safeResults = resultsByYear && typeof resultsByYear === "object" ? resultsByYear : {};
  const currentYear = new Date().getFullYear();
  const lastThreeYears = [currentYear - 2, currentYear - 1, currentYear].map(String);
  const years = Object.keys(safeResults).filter((y) => lastThreeYears.includes(y));

  const fuelTypes = {};
  Object.entries(ALL_FUEL_TYPES).forEach(([key, cfg]) => {
    const hasData = years.some((year) =>
      months.some((month) => {
        const monthData = safeResults?.[year]?.[month];
        return (monthData?.[cfg.c]?.total || 0) > 0 || (monthData?.[cfg.v]?.total || 0) > 0;
      })
    );
    if (hasData) fuelTypes[key] = cfg;
  });

  return { years, fuelTypes };
}

export function buildFuelsIntensityData(resultsByYear, t, selectedFuel) {
  const safeResults = resultsByYear && typeof resultsByYear === "object" ? resultsByYear : {};
  const { years, fuelTypes } = buildFuelsIntensityBase(resultsByYear);
  const intensityData = [];
  const averageData = [];

  const cfg = selectedFuel ? fuelTypes[selectedFuel] : null;
  if (cfg) {
    years.forEach((year) => {
      const intensities = months.map((m) => calcIntensity(safeResults?.[year]?.[m] || {}, cfg));
      const currentYear = new Date().getFullYear();
      const isCurrentYear = Number(year) === currentYear;
      const validCount = intensities.filter((v) => v > 0).length;
      const divisor = isCurrentYear ? (validCount || 1) : 12;
      const avg = intensities.reduce((a, b) => a + b, 0) / divisor;

      months.forEach((m, i) => {
        if (intensities[i] > 0) {
          intensityData.push({ year, month: t ? t(m) : m, value: intensities[i], type: `Consumo ${year}`, fuel: cfg.label });
        }
        averageData.push({ year, month: t ? t(m) : m, value: avg, type: `-- Promedio ${year}`, fuel: cfg.label });
      });
    });
  }

  return { years, fuelTypes, intensityData, averageData, yearColorMap: buildYearColorMap(years) };
}
